import { useMemo } from "react";

import clsx from "clsx";
import { cloneDeep, uniqBy } from "lodash";
import moment from "moment";

import { SecondaryButton, Spinner } from "@components/v2";
import { usePrettyLogEventPage } from "@components/v2/analytics/hooks";
import { type BenchmarkIndicatorRow } from "@features/universal-benchmarks";
import {
  UniversalBenchmarksIndicator,
  useUniversalBenchmarksRow,
} from "@features/universal-benchmarks/consumer";
import { useDesignElementManagerV2 } from "@hooks/use-design-element-manager";
import { useUserAttributionSettings } from "@hooks/use-user-attribution-settings";
import { type SalesMetricRow } from "@north-beam/nb-common";
import { type CellContext } from "@tanstack/react-table";
import { generateAnonBase64Hash } from "@utils/anonymous";
import { usePrefs } from "@utils/client-side-preferences";

import {
  useBreakdownDimensions,
  useSalesV4Context,
  useTableStateDimensions,
} from "../hooks";
import { type TableState } from "../types";
import { getSelectionStateForSelectedTab, selectedTabToLevel } from "../utils";

export const BreakdownNameColumn = ({
  cell,
  tableState,
  onTableStateChange,
  isMaximized,
}: {
  cell: CellContext<SalesMetricRow, string>;
  tableState: TableState;
  onTableStateChange: (tableState: TableState) => void;
  isMaximized?: boolean;
}) => {
  const { isDemoMode } = usePrefs();
  const { designElementManager } = useDesignElementManagerV2();

  const {
    selectedBreakdownIds,
    tableStates,
    timeGranularity,
    universalBenchmarksStage,
    setTableStates,
  } = useSalesV4Context();

  const { attributionModel, attributionWindow, accountingMode } =
    useUserAttributionSettings();

  const { idColumn, nameColumn, platformDimension, breakdownDimension } =
    useTableStateDimensions(tableState);
  const breakdownDimensions = useBreakdownDimensions();
  const rowId = idColumn.getValue(cell.row.original);
  let value = nameColumn.getValue?.(cell.row.original) ?? "";
  const platform = platformDimension.getValue?.(cell.row.original);

  const drillDownBreakdown = useMemo(() => {
    const drillDownLevel = selectedBreakdownIds.indexOf(
      breakdownDimension.id ?? "",
    );
    const drillDownBreakdownId = selectedBreakdownIds[drillDownLevel + 1];
    return breakdownDimensions.find((e) => e.id === drillDownBreakdownId);
  }, [breakdownDimensions, breakdownDimension, selectedBreakdownIds]);

  if (nameColumn.id === "date") {
    if (timeGranularity === "hourly") {
      value = moment(value).format("MMM D, YYYY, hA");
    }

    if (timeGranularity === "daily" || timeGranularity === "weekly") {
      value = moment(value).format("MMM D, YYYY");
    }

    if (timeGranularity === "monthly") {
      value = moment(value).format("MMM YYYY");
    }
  }

  const benchmarkBreakdowns = useMemo(() => {
    if (cell.row.getIsPinned()) {
      return [];
    }

    if (breakdownDimension.id === "date") {
      return [];
    }

    return [
      {
        [breakdownDimension.name]:
          breakdownDimension.getValue(cell.row.original) ?? "",
      },
    ];
  }, [cell.row, breakdownDimension]);

  const breakdownFilters = useMemo(() => {
    const filters = cloneDeep(tableState.breakdownFilters);

    if (
      cell.row.getIsPinned() &&
      tableState.selectedTab !== "breakdown" &&
      breakdownDimension.id !== "date"
    ) {
      for (const selectionRow of tableState.breakdownSelection) {
        const breakdown = breakdownDimension.name;
        const value = selectionRow.label;

        filters.push({ breakdown, value });
      }
    }

    return uniqBy(filters, (e) => `${e.breakdown}${e.value}`);
  }, [
    cell.row,
    breakdownDimension,
    tableState.breakdownFilters,
    tableState.breakdownSelection,
  ]);

  const universalBenchmarks = useUniversalBenchmarksRow({
    level: selectedTabToLevel(tableState.selectedTab),
    attributionModel,
    attributionWindow,
    accountingMode,
    breakdowns: benchmarkBreakdowns,
    filters: breakdownFilters,
  });

  const benchmarkKpis = useMemo(() => {
    if (!universalBenchmarks || universalBenchmarks.status === "loading") {
      return null;
    }

    const rv: BenchmarkIndicatorRow[] = [];

    universalBenchmarks.benchmarks.forEach((benchmark) => {
      rv.push({
        metricId: benchmark.metricV2.id,
        name: benchmark.metricV2.name,
        current: benchmark.metricV2.getValue(cell.row.original.metrics) ?? 0,
        target: benchmark.value,
        format: benchmark.metricV2.format,
        positiveTrend: benchmark.metricV2.positiveTrend,
      });
    });

    return rv;
  }, [cell.row.original.metrics, universalBenchmarks]);

  const prettyLogEventPage = usePrettyLogEventPage();

  const lastBreakdownFilter =
    tableState.breakdownFilters[tableState.breakdownFilters.length - 1];

  const benchmarkTargets = useMemo(() => {
    return benchmarkKpis?.map((benchmark) => benchmark.target) ?? [];
  }, [benchmarkKpis]);

  return (
    <div
      className={clsx(
        "border-r-1 border-x-v2-tertiary px-4 py-2.5 h-full bg-inherit text-v2-primary",
        "flex items-center gap-3 text-sm w-full group",
      )}
    >
      {cell.row.getIsPinned() ? (
        <span className="truncate font-bold text-v2-secondary flex-1">
          {isMaximized
            ? lastBreakdownFilter?.value ?? "Grand Total"
            : "Grand Total"}
        </span>
      ) : (
        <div className="flex-1 flex items-center gap-3 truncate">
          {platform &&
            designElementManager.getDesignElement(platform).getComponent()}
          <button
            className="text-v2-action hover:text-v2-action-hover"
            onClick={() => {
              if (rowId) {
                let nextTab = tableState.selectedTab;

                if (tableState.selectedTab === "breakdown") {
                  nextTab = "campaign";
                }

                if (tableState.selectedTab === "campaign") {
                  nextTab = "adset";
                }

                if (tableState.selectedTab === "adset") {
                  nextTab = "ad";
                }

                onTableStateChange({
                  ...getSelectionStateForSelectedTab(tableState, [
                    {
                      id: rowId,
                      label: value,
                    },
                  ]),
                  selectedTab: nextTab,
                });
              }
            }}
          >
            <span className="truncate">
              {generateAnonBase64Hash({
                value,
                prefix: tableState.selectedTab,
                doAnon: isDemoMode && tableState.selectedTab !== "breakdown",
              })}
            </span>
          </button>
        </div>
      )}
      {breakdownDimension.isDrillable &&
        drillDownBreakdown &&
        value &&
        tableState.selectedTab === "breakdown" && (
          <SecondaryButton
            className="gap-1.5 h-[24px] px-2 text-[12px] hidden group-hover:flex"
            size="sm"
            onClick={() => {
              prettyLogEventPage({
                domain: "Sales Attribution",
                action: "onClick",
                additionalActionInfo: "Drill down to Breakdown",
                data: {
                  pageContext: {
                    ...tableState,
                    accountingMode,
                    attributionModel,
                    attributionWindow,
                  },
                },
              });

              setTableStates([
                ...tableStates,
                {
                  ...cloneDeep(tableState),
                  selectedBreakdownId: drillDownBreakdown.id,
                  sorting:
                    drillDownBreakdown.id === "date" &&
                    tableState.selectedTab === "breakdown"
                      ? [{ id: "name", desc: false }]
                      : cloneDeep(tableState.sorting),
                  breakdownFilters: [
                    ...tableState.breakdownFilters.filter(
                      (e) => e.breakdown !== breakdownDimension.name,
                    ),
                    {
                      breakdown: breakdownDimension.name ?? "",
                      value:
                        breakdownDimension.getValue(cell.row.original) ?? "",
                    },
                  ],
                  breakdownSelection: [],
                  campaignSelection: [],
                  adsetSelection: [],
                  adSelection: [],
                  pageIndex: 0,
                },
              ]);
            }}
          >
            <i className="fa-solid fa-arrow-left-to-line"></i>
            {drillDownBreakdown.name}
          </SecondaryButton>
        )}
      {universalBenchmarks?.status === "loading" && (
        <Spinner className="w-3 h-3 text-v2-cosmic/400 text-2xs" />
      )}
      {benchmarkKpis &&
        (cell?.row?.original?.metrics?.spend || 0) > 0 &&
        benchmarkTargets.some((target) => target > 0) && (
          <UniversalBenchmarksIndicator
            benchmarkItemName={
              cell.row.getIsPinned()
                ? "Grand Total"
                : generateAnonBase64Hash({
                    value: nameColumn.getValue(cell.row.original) ?? "",
                    prefix: tableState.selectedTab,
                    doAnon:
                      isDemoMode && tableState.selectedTab !== "breakdown",
                  })
            }
            benchmarkIndicatorRows={benchmarkKpis}
            preferredStage={universalBenchmarksStage}
          />
        )}
    </div>
  );
};
